import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Locale, LoginInput, MeResponse, SessionUser } from "@salesboard/contracts";
import { ApiClientError, api } from "@/lib/api";

export const sessionQueryKey = ["session"] as const;

/**
 * The signed-in user, or `null` when nobody is.
 *
 * A 401 is a legitimate answer here, not an error — it is how the application learns
 * it is signed out — so it resolves to `null` rather than throwing and leaving the UI
 * in an error state on a perfectly normal first visit.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const res = await api.get<MeResponse>("/auth/me");
        return res.user;
      } catch (error) {
        if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<{ user: SessionUser }>("/auth/login", input),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
    },
  });
}

export function useDevLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<{ user: SessionUser }>("/auth/dev-login", { userId }),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
    },
  });
}

/**
 * Change the interface language.
 *
 * Self-service, unaudited, and **not** routed through the shared mutation reporter: there is
 * no toast on success, because the whole screen changing language is the confirmation — a
 * notice saying so, in the new language, would be telling the reader what they can already
 * read. A failure keeps the old language, which is equally self-evident.
 *
 * The response is the whole session user, so this replaces the cached session rather than
 * merging a field into it: one write, one source of truth, and `LocaleProvider` re-resolves on
 * the next render with no invalidation and no refetch.
 */
export function useSetLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) => api.patch<MeResponse>("/users/me/locale", { locale }),
    onSuccess: (data) => {
      queryClient.setQueryData(sessionQueryKey, data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => {
      // Set the session to null FIRST, so the route guard re-renders and redirects.
      queryClient.setQueryData(sessionQueryKey, null);

      // Then drop everything else, so the next user cannot see the previous user's
      // cached data. Deliberately not `queryClient.clear()`: clearing the whole cache
      // from inside a mutation's own onSuccess wipes that mutation mid-flight, and the
      // session update never reached the guard — sign-out appeared to do nothing.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== sessionQueryKey[0] });
    },
  });
}
